import type { RecurringItem, RecurringKind, RecurringSummary } from '@/api/recurring'
import { HERO_SHELL, HERO_TONE, useHeroFormat } from '@/components/loans/loanHero'
import { DESKTOP_QUERY, useMediaQuery } from '@/hooks/useMediaQuery'
import { cn } from '@/lib/utils'
import { nextUpLabel } from './dueLabel'
import { approxAmount } from '@/lib/recurring'

const KIND_PLURALS: Record<RecurringKind, string> = {
  BILL: 'Bills',
  EMI: 'EMIs',
  SUBSCRIPTION: 'Subscriptions',
  OTHER: 'Other',
}

// Shades of the hero bar, strongest first, so the legend stays inside the mint palette.
const SHADES = [
  'var(--fl-hero-fg)',
  'var(--fl-hero-bar)',
  'color-mix(in srgb, var(--fl-hero-bar) 60%, var(--fl-hero-bar-faint))',
  'color-mix(in srgb, var(--fl-hero-bar) 30%, var(--fl-hero-bar-faint))',
]

export function RecurringHero({
  summary,
  paymentCount,
  nextUp,
}: {
  summary: RecurringSummary
  paymentCount: number
  nextUp: RecurringItem | null
}) {
  const format = useHeroFormat()
  const wide = useMediaQuery(DESKTOP_QUERY)
  const left = summary.monthlyIncome - summary.monthlyPayments
  const short = left < 0
  const kinds = summary.paymentsByKind
    .filter((k) => k.monthlyAmount > 0)
    .sort((a, b) => b.monthlyAmount - a.monthlyAmount)

  return (
    <section
      aria-label="Recurring summary"
      className={cn(
        HERO_SHELL,
        'grid gap-3 motion-safe:animate-fade-slide-up md:grid-cols-[1.3fr_1fr] md:items-center md:gap-7'
      )}
    >
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[var(--fl-hero-fg-2)] md:text-[11.5px]">
          Goes out every month
        </p>
        <p
          className={cn(
            'mt-0.5 font-serif text-[26px] font-semibold leading-tight tabular-nums md:text-[34px]',
            HERO_TONE.expense
          )}
        >
          {format(summary.monthlyPayments)}
        </p>
        {wide ? (
          <p className="mt-0.5 text-[13px] tabular-nums text-[var(--fl-hero-fg-2)]">
            {format(summary.yearlyPaymentProjection)} a year · {paymentCount}{' '}
            {paymentCount === 1 ? 'payment' : 'payments'}
          </p>
        ) : (
          <p className="mt-0.5 text-xs tabular-nums text-[var(--fl-hero-fg-2)]">
            In {format(summary.monthlyIncome)} · {short ? 'short' : 'left'} {format(Math.abs(left))}{' '}
            · {format(summary.yearlyPaymentProjection)} a year · {paymentCount}{' '}
            {paymentCount === 1 ? 'payment' : 'payments'}
          </p>
        )}
        {kinds.length > 0 && (
          <>
            <div
              aria-hidden="true"
              className="mt-3 flex h-1.5 gap-0.5 overflow-hidden rounded-full md:h-2"
            >
              {kinds.map((k, i) => (
                <div
                  key={k.kind}
                  style={{
                    width: `${(k.monthlyAmount / summary.monthlyPayments) * 100}%`,
                    background: SHADES[i],
                  }}
                />
              ))}
            </div>
            <ul className="mt-2 flex flex-wrap gap-x-3.5 gap-y-1 text-xs text-[var(--fl-hero-fg-2)]">
              {kinds.map((k, i) => (
                <li key={k.kind} className="flex items-center gap-1.5">
                  <span
                    aria-hidden="true"
                    className="h-2 w-2 rounded-full"
                    style={{ background: SHADES[i] }}
                  />
                  {KIND_PLURALS[k.kind]}
                  <span className="tabular-nums text-[var(--fl-hero-fg)]">
                    {format(k.monthlyAmount)}
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
      {wide && (
        <dl className="grid grid-cols-2 gap-x-5 gap-y-3.5 text-[13px]">
          <div>
            <dt className="text-xs text-[var(--fl-hero-fg-2)]">Comes in every month</dt>
            <dd
              className={cn(
                'mt-0.5 font-serif text-xl font-semibold tabular-nums',
                HERO_TONE.income
              )}
            >
              {format(summary.monthlyIncome)}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-[var(--fl-hero-fg-2)]">
              {short ? 'Short each month' : 'Left after commitments'}
            </dt>
            <dd
              className={cn(
                'mt-0.5 font-serif text-xl font-semibold tabular-nums',
                short && HERO_TONE.expense
              )}
            >
              {format(Math.abs(left))}
            </dd>
          </div>
          {nextUp && (
            <div className="col-span-2">
              <dt className="text-xs text-[var(--fl-hero-fg-2)]">Next up</dt>
              <dd className="mt-0.5 font-semibold">
                {nextUp.name} · {approxAmount(nextUp, format(nextUp.amount))} ·{' '}
                {nextUpLabel(nextUp.nextDueDate)}
              </dd>
            </div>
          )}
        </dl>
      )}
    </section>
  )
}
